import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../../utils/seo';

// ===== 危機管理研修 詳細 全面作り直し（Implementer C スコープ） =====
// デザイン土台: home / about/overview の新スタイル。実データ変更禁止・現行working tree実文のみ・創作禁止。

function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";
const TEXT_ARROW = "inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] tracking-[0.06em] border-b border-[#a38e65] pb-1 hover:text-[#8f7a56] transition-colors duration-300";

const OVERVIEW = [
  '執事は「最悪の事態を想定し、最善の準備をする」プロフェッショナルです。富裕層のゲストを守る立場として、火災、自然災害、医療緊急事態、セキュリティ上の脅威、食中毒、設備故障など、あらゆるリスクを事前に評価し、適切な対策を講じます。万が一の事態が発生した場合も、パニックに陥ることなく冷静に判断し、的確に行動する能力が求められます。',
  '本研修では、執事の危機管理手法を理論と実践の両面から学びます。リスクアセスメント（危険予測）の体系的手法、潜在的リスクの早期発見、予防策の立案と実施、緊急時の意思決定プロセス、避難誘導とゲスト保護、医療緊急事態への対応、事後対応とリカバリー、再発防止策の策定まで、実務で即活用できる内容を習得。シミュレーション演習とケーススタディを通じて、実践的な危機対応力を体得します。',
  '高級ホテル、レストラン、イベント施設、富裕層向けサービス業など、ゲストの安全を守る責任を持つすべての業種の方に最適な研修です。',
];

const FEATURES = [
  { icon: 'ri-shield-check-line', t: '予防重視', d: '事前のリスク評価と対策を学びます。' },
  { icon: 'ri-play-circle-line', t: '実践的訓練', d: 'シミュレーション演習で体得します。' },
  { icon: 'ri-brain-line', t: '冷静な判断力', d: '緊急時の意思決定を磨きます。' },
];

const CURRICULUM = [
  { part: '第1部', time: '45分', title: 'リスクアセスメントの基礎', desc: '危機管理の基本原則、執事が実践するリスク評価手法、潜在的危険の早期発見、予防策の立案と優先順位付けを学びます。執事が日常的に行う「危険の芽」を摘み取る技術、リスクマップの作成方法、予防策の効果的な実施方法を習得します。理論だけでなく、実際の執事の現場での事例を通じて、効果的なリスク管理のあり方を深く学びます。' },
  { part: '第2部', time: '45分', title: '緊急時対応プロトコル', desc: '火災・地震などの災害対応、医療緊急事態への対処、セキュリティ上の脅威への対応、避難誘導の技術を習得します。執事が実践する「冷静な判断」と「迅速な行動」の技術、緊急時のコミュニケーション方法、ゲストの安全を最優先に考えた対応手順を学びます。実際のシナリオを用いた演習を通じて実践的なスキルを身につけます。' },
  { part: '第3部', time: '45分', title: '冷静な判断と意思決定', desc: '緊急時の心理状態の理解、パニックの防止と冷静さの維持、限られた情報での意思決定、コミュニケーションと指示の技術を学びます。執事が実践する「感情をコントロールする」技術、プレッシャー下での正確な判断力、チーム全体を落ち着かせるリーダーシップなど、危機的状況でのメンタル管理の方法を習得します。' },
  { part: '第4部', time: '45分', title: 'シミュレーション演習とケーススタディ', desc: '実践的なシミュレーション、事後対応と再発防止、実際のケースを用いた演習で実践力を養います。執事が経験した実際の危機事例を基にしたケーススタディ、グループでの問題解決演習、事後の振り返りと改善策の検討を行います。実際のケーススタディを通じて、現場で使える危機対応スキルを身につけます。' },
];

const RECOMMENDS = [
  '高級ホテル・旅館の全スタッフ',
  'レストラン・宴会場の責任者',
  'イベント施設・コンサートホール',
  '富裕層向けサービス業',
  'VIP対応スタッフ',
  '警備・セキュリティ部門',
  '施設管理責任者',
  '防災担当者',
];

const OUTCOMES = [
  { t: 'リスク予測能力の向上', d: '潜在的な危険を事前に察知し、適切な予防策を講じる力を身につけます。執事の現場経験に基づく実践的なリスク評価技術を習得します。' },
  { t: '緊急時対応力の強化', d: '万が一の事態でも冷静に判断し、的確に行動できる能力を習得します。パニックに陥らない精神力と迅速な対応技術を身につけます。' },
  { t: 'ゲスト保護の技術', d: 'VIPゲストの安全を最優先に守る具体的な手法を学びます。高度なセキュリティ意識と保護技術を習得します。' },
  { t: '組織の安全文化醸成', d: 'スタッフ全体の危機管理意識を高め、安全な職場環境を実現します。組織全体のセキュリティレベルを向上させる技術を身につけます。' },
];

const SPECS = [
  { k: '研修時間', v: '1日3時間（午前/午後選択可）' },
  { k: '対象人数', v: '10-20名' },
  { k: '対象レベル', v: '中級〜上級（管理職・責任者）' },
  { k: '実施場所', v: '貴社会議室または当協会会場' },
  { k: 'カスタマイズ', v: '内容調整可能（別途相談）' },
];

const INCLUDES = ['研修プログラム実施', 'テキスト教材（参加者人数分）', '危機管理チェックリスト', '修了証（参加者全員）', '講師交通費（都内の場合）', '事後フォローアップ（1回）'];

const FLOW = [
  { t: 'お問い合わせ・ご相談', d: 'まずはお気軽にお問い合わせください。貴社の課題や目標をヒアリングいたします。' },
  { t: 'ヒアリング・カスタマイズ', d: '施設の特性、想定されるリスク、現状の対策などを詳しく伺い、最適なプログラムを設計します。' },
  { t: '日程調整・ご契約', d: '実施日時、場所、料金を確定し、ご契約となります。' },
  { t: '研修実施', d: '経験豊富な講師による実践的な研修を実施します。' },
  { t: 'フォローアップ', d: '研修後も継続的にフォローアップし、現場での実践をサポートします。' },
];

const FAQS = [
  { q: 'オンラインでの実施は可能ですか？', a: '本研修は実践的なシミュレーションを含むため、対面実施を推奨しています。' },
  { q: '消防訓練との違いは何ですか？', a: '消防訓練に加え、医療緊急事態やセキュリティなど幅広いリスクを扱います。' },
  { q: '実際のシミュレーション演習はどのような内容ですか？', a: '地震発生時の避難誘導、体調不良者への対応など、想定ケースを実践します。' },
  { q: 'ホテル以外の業種でも役立ちますか？', a: 'はい、ゲストの安全を守るすべてのサービス業に共通する内容です。' },
  { q: '実施場所はどこになりますか？', a: '貴社施設での実施が基本です。実際の環境での訓練が効果的です。' },
  { q: '事前準備は必要ですか？', a: '貴社の現行の防災マニュアルや過去のインシデント記録をご用意ください。' },
  { q: 'フォローアップの内容は？', a: '研修後1ヶ月以内に、実践状況を確認し、改善提案を行います。' },
  { q: '修了証は発行されますか？', a: 'はい、参加者全員に修了証を発行いたします。' },
  { q: '他の研修と組み合わせることは可能ですか？', a: 'はい。複数研修の組み合わせも可能です。割引もございます。' },
  { q: '講師はどのような方ですか？', a: '危機管理の実務経験を持つMaster Butler資格保持者が担当します。' },
  { q: '医療緊急事態への対応も学べますか？', a: 'はい、第2部で基本的な応急処置と医療機関への連絡方法を学びます。' },
  { q: 'セキュリティ上の脅威とは具体的に何ですか？', a: '不審者、盗難、プライバシー侵害など、VIPゲストを脅かす要因です。' },
  { q: '食中毒への対応も含まれますか？', a: 'はい、飲食を伴う施設向けには食品衛生面のリスク管理も扱います。' },
  { q: '研修後に追加の訓練は必要ですか？', a: '定期的な復習訓練の実施をお勧めしています。継続支援も可能です。' },
];

const RELATED = [
  { label: 'チームリーダーシップ研修', href: '/seminars/corporate/team-leadership', desc: '執事に学ぶマネジメント力と組織運営の技術' },
  { label: 'スケジュール管理研修', href: '/seminars/corporate/schedule-management', desc: '執事に学ぶ時間管理と優先順位設定の技術' },
  { label: '企業向け研修プログラム一覧', href: '/seminars', desc: 'すべての研修プログラムを確認する' },
];

export default function CrisisManagementPage() {
  useEffect(() => {
    document.title = 'クライシスマネジメント研修｜危機管理スキル向上｜全日本執事協会';
    updateSEO({
      title: 'クライシスマネジメント研修｜危機管理スキル向上｜全日本執事協会',
      description: '全日本執事協会のクライシスマネジメント研修。危機管理スキルの向上とリスク対応能力を身につける専門プログラム。企業研修として最適な実践的カリキュラムを提供します。',
      canonical: 'https://butler-association.jp/seminars/corporate/crisis-management',
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'セミナー・イベント', href: '/seminars' },
    { label: '危機管理研修' },
  ];

  return (
    <Layout>
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-corp-crisis-management.webp" alt="危機管理研修で緊急時対応の手順を指導する執事と受講者" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Crisis Management</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.4] tracking-[0.04em] mb-6">危機管理研修</h1>
            <div className="space-y-5 font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              <p>
                執事が日々実践している高度な危機管理能力を、現代のサービス業に応用する実践的プログラムです。執事はゲストの安全と快適さを守るため、常に周囲の状況を観察し、潜在的なリスクを察知し、事前に対策を講じます。
              </p>
              <p>
                火災、地震、体調不良、セキュリティ上の脅威など、あらゆる緊急事態に冷静に対処し、被害を最小限に抑える技術は、特に富裕層向けサービス業において不可欠です。本研修では、リスクアセスメントの手法、緊急時の意思決定プロセス、効果的な避難誘導、予防策の立案まで、実務に直結する危機管理スキルを体系的に習得します。
              </p>
            </div>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-10">
          <p className="border-l-2 border-[#a38e65] bg-[#f8f6f0] px-5 py-4 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">
            ⚠️ 本研修はスキル習得を目的としており、事故ゼロや完全な安全を保証するものではありません。
          </p>
        </div>
      </div>

      <NavyBand no="I" ja="この研修について" en="Overview" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start mb-12 sm:mb-14">
            <div>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-6">
                最悪を想定し、<span className="text-[#a38e65]">最善を準備する</span>
              </h3>
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                {OVERVIEW.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <img src="/images/generated/jba-p-033-crisis-review.webp" alt="危機管理の予防策を確認する研修の様子" className="w-full h-64 lg:h-[380px] object-cover border border-[#a38e65]/30" />
          </div>
          <div className="grid sm:grid-cols-3 border-t-2 border-[#1a2942]">
            {FEATURES.map((f) => (
              <div key={f.t} className="flex flex-col gap-4 border-b border-[#e2ddd2] sm:border-b-0 sm:border-r last:border-r-0 py-7 sm:px-6 first:sm:pl-0">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-[#a38e65]/50 text-[#a38e65]"><i className={`${f.icon} text-2xl`}></i></span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">{f.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 新バリエーション①（番号付き時間バッジ・カリキュラム。由来: home IV 全幅エディトリアル行型の翻案） */}
      <NavyBand no="II" ja="研修カリキュラム（1日3時間）" en="Curriculum" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              危機管理研修では、執事の現場経験に基づく4つの分野を体系的に学びます。各部は実践的な内容で構成され、知識だけでなく実際の危機対応技術も習得できます。
            </p>
          </div>
          <img src="/images/generated/seq-crisis-management-training-4dad5f.jpg" alt="想定事態への初動と役割分担を話し合う危機管理研修のケース検討" className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10" />
          <div className="border-t-2 border-[#1a2942]">
            {CURRICULUM.map((c) => (
              <div key={c.part} className="flex flex-col md:flex-row gap-4 md:gap-10 border-b border-[#e2ddd2] py-7">
                <div className="md:w-[220px] md:shrink-0">
                  <div className="flex items-center gap-3">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl leading-none">{c.part}</span>
                    <span className="inline-block border border-[#a38e65]/60 text-[#8f7a56] text-xs tracking-[0.06em] px-2.5 py-1 font-['Noto_Serif_JP',serif]">{c.time}</span>
                  </div>
                  <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mt-3">{c.title}</h4>
                </div>
                <p className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="III" ja="こんな企業・部門におすすめ" en="Ideal For" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid sm:grid-cols-2 border-t border-[#e2ddd2]">
            {RECOMMENDS.map((r) => (
              <div key={r} className="flex items-center gap-4 border-b border-[#e2ddd2] py-5 sm:px-5 first:sm:pl-0">
                <i className="ri-check-line text-xl text-[#a38e65] shrink-0"></i>
                <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942]">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="IV" ja="研修で得られる成果" en="Outcomes" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <img src="/images/generated/jba-p-034-crisis-calm-v2.webp" alt="研修を経て高まった危機対応力を示す受講者の様子" className="w-full aspect-[21/9] h-auto object-cover object-top border border-[#a38e65]/30 mb-10" />
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {OUTCOMES.map((o, i) => (
              <div key={o.t} className="bg-[#1a2942] border border-[#a38e65]/45 p-7 sm:p-8 flex flex-col">
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none">{String(i + 1).padStart(2, '0')}</span>
                  <h4 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl leading-[1.5]">{o.t}</h4>
                </div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.95]">{o.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 新バリエーション②（大数値+細罫スペック表+含有グリッド。由来: home IV Fee強調 + home VII 細罫テーブル） */}
      <NavyBand no="V" ja="実施形式と料金" en="Details & Fee" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[360px_1fr] gap-10 lg:gap-14 items-start">
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 text-center">
              <div className="font-['Noto_Serif_JP',serif] text-sm text-[#d7b779] tracking-[0.1em] mb-3">受講料（税込）</div>
              <div className="font-['Playfair_Display',serif] text-[#f8f6f0] text-5xl sm:text-6xl leading-none">400,000<span className="text-2xl ml-1">円</span></div>
              <div className="w-11 h-px bg-[#a38e65] mx-auto mt-6 mb-5"></div>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">1日3時間・10-20名でのご提供です。</p>
            </div>
            <div>
              <dl className="border-t-2 border-[#1a2942]">
                {SPECS.map((s) => (
                  <div key={s.k} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                    <dt className="sm:w-[160px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{s.k}</dt>
                    <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{s.v}</dd>
                  </div>
                ))}
              </dl>
              <h4 className="jba-display font-medium text-[#1a2942] text-lg mt-10 mb-5">料金に含まれるもの</h4>
              <ul className="grid sm:grid-cols-2 gap-x-8">
                {INCLUDES.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 border-b border-[#e2ddd2] py-3 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><i className="ri-check-line text-[#a38e65]"></i><span>{inc}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <NavyBand no="VI" ja="お申込みの流れ" en="How to Apply" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <ol className="relative border-l border-[#e2ddd2] ml-5 max-w-4xl">
            {FLOW.map((s, i) => (
              <li key={s.t} className="relative pl-8 sm:pl-10 pb-9 last:pb-0">
                <span className="absolute -left-[19px] top-0 w-9 h-9 rounded-full bg-[#1a2942] text-[#d7b779] flex items-center justify-center font-['Playfair_Display',serif] text-sm border border-[#a38e65]/50">{i + 1}</span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg mb-1.5">{s.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
              </li>
            ))}
          </ol>
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 mt-12 text-center">
            <h4 className="jba-display font-medium text-xl sm:text-2xl text-[#f8f6f0] mb-3">お申込み・お問い合わせ</h4>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] mb-7 max-w-2xl mx-auto leading-[1.9]">危機管理研修についてのご質問やお申込みは、お気軽にお問い合わせください。</p>
            <a href="/contact" className={BTN08_ON_NAVY}>危機管理研修に申し込む</a>
          </div>
        </div>
      </section>

      <NavyBand no="VII" ja="よくある質問" en="FAQ" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="border-t-2 border-[#1a2942] max-w-4xl">
            {FAQS.map((f) => (
              <div key={f.q} className="border-b border-[#e2ddd2] py-6">
                <h3 className="font-['Noto_Serif_JP',serif] font-medium text-[#1a2942] text-base sm:text-lg leading-[1.6] mb-2.5 flex gap-3"><span className="font-['Playfair_Display',serif] text-[#a38e65]">Q.</span><span>{f.q}</span></h3>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.95] flex gap-3"><span className="font-['Playfair_Display',serif] text-[#8f7a56]">A.</span><span>{f.a}</span></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="VIII" ja="関連研修プログラム" en="Related Programs" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-8">
            {RELATED.map((r) => (
              <a key={r.href} href={r.href} className="group bg-white border border-[#e2ddd2] p-8 flex flex-col hover:border-[#a38e65]/70 transition-colors duration-300">
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 group-hover:text-[#8f7a56] transition-colors">{r.label}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9] mb-6">{r.desc}</p>
                <span className={`${TEXT_ARROW} mt-auto self-start`}>詳細を見る <span className="text-[#a38e65]">→</span></span>
              </a>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="/seminars" className={BTN08_ON_LIGHT}>セミナー・イベント一覧へ戻る</a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
